import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../../../layouts/AdminLayout';
import { add, format, startOfDay, sub, eachDayOfInterval, getDay } from 'date-fns';
import Footer from '../../../shared/components/Footer';
// import JumpToDateDialog from '../components/JumpToDateDialog';
import RestrictionSettingsDialog from '../components/RestrictionSettingsDialog';
import RestrictionSetup from '../components/RestrictionSetup';
import RoomTypeFilter from '../components/RoomTypeFilter';
import RatePlanFilter from '../components/RatePlanFilter';
import MonthlySetupTable, { SelectedCell } from '../components/MonthlySetupTable';
import RestrictionFilter from '../components/RestrictionFilter';
import useGetAllRoomType from '../../../services/room-type/hooks/useGetAllRoomType';
import useIndexRatePlan from '../../../services/rate-plan/hooks/useIndexRatePlan';
import { useNavigate } from 'react-router';
import { NAVIGATION_URLS } from '../../../shared/utils/urls';
import { IconCalendarStats, IconSettings, IconX, IconFileImport } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import ImportRatesDialog from '../components/ImportRatesDialog';
import RateImportProgress from '../components/RateImportProgress';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import { useAuth } from '../../../auth/context/AuthProvider';
import { toast } from 'sonner';
import { GroupBase } from 'react-select';

// Import komponen dan hooks baru
import DayOfWeekSelector from '../components/DayOfWeekSelector';
import RestrictionTypeSelector from '../components/RestrictionTypeSelector';
import useOccupancyDailyV4 from '../../../services/availability-and-rates/hooks/useOccupancyDailyV4';
import { FormattedRoom, useFormattedMonthlySetup } from '../hooks/useFormattedMonthlySetup';
import RatePlanSelector, { RatePlanOption } from '../components/RatePlanSelector';

// BARU: Import modal yang baru dibuat
import BulkUpdateByDateRangeDialog from '../components/BulkUpdateByDateRangeDialogV2';
import JumpToDateDialogV2 from '../components/JumpToDateDialogV2';
import JumpToDateRangeControl from '../components/JumpToDateRangeControl';
import ReadOnlyGuard from '../../../shared/components/ReadOnlyGuard';
import PropertyInactiveBanner from '../../../shared/components/PropertyInactiveBanner';
import { usePropertyReadOnly } from '../../../shared/hooks/usePropertyReadOnly';


// Placeholder untuk komponen FullPageNoData
const FullPageNoData: React.FC = () => {
    const navigate = useNavigate();
    const handleSetupRoomTypes = () => {
        navigate(NAVIGATION_URLS.DASHBOARD_ADMIN_PROPERTY_SETTINGS_RATE);
    };
    return (
        <div className='d-flex justify-content-center align-items-center' style={{ height: '100%', minHeight: '100vh' }}>
            <div className='text-center'>
                <IconSettings size={72} />
                <p className='mt-3'>Please setup room types to see the calendar</p>
                <button className='btn btn-primary' onClick={handleSetupRoomTypes}>
                    Setup Room Types
                </button>
            </div>
        </div>
    );
};

type OptionType = { value: string; label: string; };

const MonthlySetupPage: React.FC = () => {
    const { user } = useAuth();
    const { isReadOnly } = usePropertyReadOnly();
    const [startDate, setStartDate] = useState<string>('');
    const [endDate, setEndDate] = useState<string>('');

    // Filters
    const [restrictionFilter, setRestrictionFilter] = useState<string[]>([]);
    const [roomTypeFilter, setRoomTypeFilter] = useState<string[]>([]);
    const [ratePlanFilter, setRatePlanFilter] = useState<string[]>([]);

    // State untuk menyimpan sel yang dipilih
    const [selectedCells, setSelectedCells] = useState<SelectedCell[]>([]);

    // State untuk filter hari dan tipe
    const [selectedDays, setSelectedDays] = useState<string[]>([]);
    const [selectedRestrictionType, setSelectedRestrictionType] = useState<OptionType | null>(null);

    // State untuk dropdown rate plan target
    const [targetRatePlan, setTargetRatePlan] = useState<RatePlanOption | null>(null);

    // ====================================================================
    // BARU: State untuk melacak sel yang baru diubah
    // ====================================================================
    const [editedCells, setEditedCells] = useState<SelectedCell[]>([]);

    // ====================================================================
    // BARU: State untuk fitur Drag-to-Select
    // ====================================================================
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const [dragStartCell, setDragStartCell] = useState<SelectedCell | null>(null);

    // BARU: State untuk modal baru
    const [showBulkUpdateByDateDialog, setShowBulkUpdateByDateDialog] = useState<boolean>(false);
    const [showImportRatesDialog, setShowImportRatesDialog] = useState<boolean>(false);
    // Import id yang sedang diproses di background (setelah modal ditutup saat processing).
    const [backgroundImportId, setBackgroundImportId] = useState<string | null>(null);
    const queryClient = useQueryClient();

    const [isFilterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);

    useEffect(() => {
        const today = startOfDay(new Date());
        const date30Days = add(today, { days: 30 });
        const formattedToday = format(today, 'yyyy-MM-dd');
        const formatted30DaysFromNow = format(date30Days, 'yyyy-MM-dd');
        setStartDate(formattedToday);
        setEndDate(formatted30DaysFromNow);
    }, []);

    const [showJumpToDateDialog, setShowJumpToDateDialog] = useState<boolean>(false);
    const [showRestrictionsDialog, setShowRestrictionsDialog] = useState<boolean>(false);

    // Data fetching
    const roomTypeQuery = useGetAllRoomType({ all: true });
    const { data: roomData } = roomTypeQuery;

    const ratePlanQuery = useIndexRatePlan({ include: 'roomType', all: true });

    const { data: occupancy, isFetching: isFetchingOccupancy, isError: isErrorOccupancy, isSuccess: isSuccessOccupancy } = useOccupancyDailyV4({ property_id: user?.active_property_id || '', start: startDate, end: endDate });
    const {
        data: monthlySetup = [],
        isFetching: isFetchingMonthlySetup,
        isError: isErrorMonthlySetup,
        isSuccess: isSuccessMonthlySetup,
        dynamicPricingGroups,
        priceAdjustments
    } = useFormattedMonthlySetup({ startDate, endDate, property_id: user?.active_property_id || '' }, roomTypeQuery, ratePlanQuery);

    const allDaysInRange = (startDate && endDate) ? eachDayOfInterval({ start: new Date(startDate), end: new Date(endDate) }) : [];
    const groupedMonths = allDaysInRange.reduce<Record<string, Date[]>>((acc, day) => {
        const monthYear = format(day, 'MMMM yyyy');
        if (!acc[monthYear]) acc[monthYear] = [];
        acc[monthYear].push(day);
        return acc;
    }, {});

    const readyToLoad =
        isSuccessMonthlySetup && monthlySetup && !isFetchingMonthlySetup && !isErrorMonthlySetup &&
        isSuccessOccupancy && occupancy && !isFetchingOccupancy && !isErrorOccupancy;

    // Memoize untuk membuat opsi dropdown RatePlanSelector
    const ratePlanSelectorOptions = useMemo<GroupBase<RatePlanOption>[]>(() => {
        if (!monthlySetup || monthlySetup.length === 0) return [];

        return monthlySetup
            .filter(room => roomTypeFilter.includes(room.id)) // Filter berdasarkan room type filter utama
            .map(room => ({
                label: room.name, // Nama grup adalah nama kamar
                options: room.rates
                    .filter(rate => ratePlanFilter.includes(rate.id) && rate.type !== 'link') // Filter berdasarkan rate plan filter utama & bukan link
                    .map(rate => ({
                        value: rate.id,
                        label: rate.name,
                        roomName: room.name,
                    })),
            }))
            .filter(group => group.options.length > 0); // Hanya tampilkan grup yang punya opsi
    }, [monthlySetup, roomTypeFilter, ratePlanFilter]);

    // useEffect untuk mereset target rate plan jika filternya berubah
    useEffect(() => {
        setTargetRatePlan(null);
        handleClearSelection(); // Bersihkan juga seleksi saat filter utama berubah
    }, [roomTypeFilter, ratePlanFilter]);


    // useEffect untuk menangani pemilihan massal
    useEffect(() => {
        // Guard Clauses: Jangan jalankan jika input tidak lengkap
        if (!targetRatePlan || !selectedRestrictionType || selectedDays.length === 0 || !monthlySetup) {
            return;
        }

        // BARU: Hapus highlight 'edited' saat memulai bulk select baru
        setEditedCells([]);

        const targetRateId = targetRatePlan.value;
        const newSelectionType = selectedRestrictionType.value;

        const DAYS_MAP: { [key: string]: number } = { sun: 0, mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6 };
        const selectedDayNumbers = selectedDays.map(dayKey => DAYS_MAP[dayKey]);

        const configMapForSelection: any = { cta: { type: 'closed_to_arrival', inherit: 'inherit_closed_to_arrival' }, ctd: { type: 'closed_to_departure', inherit: 'inherit_closed_to_departure' }, ss: { type: 'stop_sell', inherit: 'inherit_stop_sell' }, mxs: { type: 'max_stay', inherit: 'inherit_max_stay' }, msa: { type: 'min_stay_arrival', inherit: 'inherit_min_stay_arrival' }, mst: { type: 'min_stay_through', inherit: 'inherit_min_stay_through' }, rate: { type: 'rate', inherit: 'inherit_restriction' } };
        const config = configMapForSelection[newSelectionType];

        if (!config) return;

        const newCells: SelectedCell[] = [];

        monthlySetup.forEach((room: FormattedRoom) => {
            room.rates.forEach((rate: any) => {
                if (rate.id !== targetRateId) {
                    return;
                }
                if (rate.type === 'link' || rate[config.inherit]) {
                    return;
                }
                rate.restictions.forEach((restriction: any) => {
                    const currentDate = new Date(restriction[0]);
                    if (selectedDayNumbers.includes(getDay(currentDate))) {
                        newCells.push({
                            rateId: rate.id,
                            date: format(currentDate, 'yyyy-MM-dd'),
                            rateType: rate.type,
                            restrictionType: config.type,
                        });
                    }
                });
            });
        });

        setSelectedCells(newCells);
        if (newCells.length > 0) {
            toast.success(`${newCells.length} cells selected for '${targetRatePlan.label}' on '${selectedRestrictionType.label}'.`, { id: 'bulk-select-info' });
        } else {
            toast.info(`No eligible cells found for the selected day(s) and type in this rate plan.`, { id: 'bulk-select-info' });
        }

        setSelectedDays([]);
        setSelectedRestrictionType(null);

    }, [targetRatePlan, selectedRestrictionType, selectedDays, monthlySetup]);

    // BARU: useEffect untuk menangani akhir dari proses drag (mouseup)
    useEffect(() => {
        const handleMouseUp = () => {
            if (isDragging) {
                setIsDragging(false);
                setDragStartCell(null);
            }
        };

        // Listener ini dipasang di level window agar bisa menangkap mouseup di mana saja
        window.addEventListener('mouseup', handleMouseUp);

        return () => {
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging]); // Dependensi isDragging memastikan listener selalu up-to-date

    // Navigation Handlers
    const navigateWithConfirmation = (navigationAction: () => void, message: string) => {
        if (selectedCells.length > 0) {
            if (window.confirm(message)) {
                handleClearSelection();
                setTargetRatePlan(null);
                setEditedCells([]); // BARU: Hapus highlight saat navigasi
                navigationAction();
            }
        } else {
            setEditedCells([]); // BARU: Hapus highlight saat navigasi bahkan jika tidak ada seleksi
            navigationAction();
        }
    };

    const handleNextMonth = () => {
        const action = () => {
            setStartDate((prevState) => format(add(new Date(prevState), { days: 30 }), 'yyyy-MM-dd'));
            setEndDate((prevState) => format(add(new Date(prevState), { days: 30 }), 'yyyy-MM-dd'));
        };
        navigateWithConfirmation(action, 'You have active selections. Moving to the next month will clear them. Are you sure?');
    };

    const handlePrevMonth = () => {
        const action = () => {
            setStartDate((prevState) => format(sub(new Date(prevState), { days: 30 }), 'yyyy-MM-dd'));
            setEndDate((prevState) => format(sub(new Date(prevState), { days: 30 }), 'yyyy-MM-dd'));
        };
        navigateWithConfirmation(action, 'You have active selections. Moving to the previous month will clear them. Are you sure?');
    };

    const handleThisMonth = () => {
        const action = () => {
            setStartDate(format(startOfDay(new Date()), 'yyyy-MM-dd'));
            setEndDate(format(add(startOfDay(new Date()), { days: 30 }), 'yyyy-MM-dd'));
        };
        navigateWithConfirmation(action, 'You have active selections. Jumping to this month will clear them. Are you sure?');
    };

    // Dialog Handlers
    const [showRestrictionSetupDialog, setShowRestrictionSetupDialog] = useState<boolean>(false);

    const handleOpenBulkEditDialog = () => {
        const authorized = user?.role?.includes('dev') || user?.permissions?.includes('monthly_setup_restriction_setting');
        if (!authorized) {
            toast.error("You don't have permission to perform this action.");
            return;
        }
        if (isReadOnly) return;
        if (selectedCells.length === 0) {
            toast.info("Please select one or more cells to edit.");
            return;
        }
        setShowRestrictionSetupDialog(true);
    };

    const handleCloseRestrictionSetupDialog = () => {
        setShowRestrictionSetupDialog(false);
    };

    // Selection Handlers
    const handleClearSelection = () => {
        if (selectedCells.length > 0) {
            setSelectedCells([]);
            toast.info("Selection cleared.");
        }
        setSelectedDays([]);
        setSelectedRestrictionType(null);
    };

    
    const handleBulkUpdateSuccess = (affectedCells: SelectedCell[]) => {
        setShowBulkUpdateByDateDialog(false);
        toast.info("Applying updates, calendar will refresh shortly...");

        // PERBAIKAN: Terima data dan set state `editedCells`
        setEditedCells(affectedCells);
    };

    if (roomData?.data.length === 0) {
        return (
            <AdminLayout>
                <FullPageNoData />
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="page mt-1 bg-white min-vh-100">
                <div className={`page-body mt-0 min-vh-100 ${isDragging ? 'is-dragging' : ''}`}>
                    <div className="container-fluid px-0">
                        <PropertyInactiveBanner variant="full" />
                        {/* ======================================================================= */}
                        {/* STRUKTUR HEADER BARU (Desktop & Mobile)                   */}
                        {/* ======================================================================= */}

                        {/* // Aksi 1: Bagian filter dan pengaturan yang hanya terlihat di desktop (lg ke atas) */}
                        <div className="d-none d-lg-block border-bottom">
                            {/* Baris 1: Navigasi & Tampilan */}
                            <div className="d-flex justify-content-between align-items-center p-2">
                                <div className="d-flex align-items-center gap-2">
                                    <h6 className="mb-0 me-2 text-muted">View Filters:</h6>
                                    <RestrictionFilter setRestrictionFilter={setRestrictionFilter} />
                                    <RoomTypeFilter setRoomTypeFilter={setRoomTypeFilter} roomTypeQuery={roomTypeQuery} />
                                    <RatePlanFilter setRatePlanFilter={setRatePlanFilter} ratePlanQuery={ratePlanQuery} />
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                    <JumpToDateRangeControl
                                        startDate={startDate}
                                        endDate={endDate}
                                        onChangeDate={(callback) => {
                                            const action = () => {
                                                setStartDate(format(new Date(callback.start_date), 'yyyy-MM-dd'));
                                                setEndDate(format(new Date(callback.end_date), 'yyyy-MM-dd'));
                                            };
                                            navigateWithConfirmation(action, 'You have active selections. Jumping to a new date range will clear them. Are you sure?');
                                        }}
                                    />
                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['settings_restrictions']} asComponent>
                                        <ReadOnlyGuard>
                                            <button className='btn btn-sm btn-primary p-2' onClick={() => setShowRestrictionsDialog(true)}>
                                                Restrictions & Settings
                                            </button>
                                        </ReadOnlyGuard>
                                    </RequireAuth>
                                </div>
                            </div>
                        </div>

                        {/* Tampilan Mobile: Terlihat di layar medium (md) ke bawah */}
                        <div className="d-lg-none p-2 border-bottom">
                            <button
                                className="btn btn-primary w-100"
                                onClick={() => setFilterDrawerOpen(true)}
                            >
                                <IconSettings size={16} className="me-2" />
                                Filters & Actions
                            </button>
                        </div>

                        {/* // Aksi 2: Bagian Aksi yang terlihat di semua perangkat (mobile dan desktop) */}
                        <div className="d-flex align-items-center gap-2 p-2 bg-light border-top border-bottom">
                            <h6 className="mb-0 me-2 text-muted d-none d-lg-inline">Actions:</h6>
                            <RequireAuth requiredRoles={['dev']} requiredPermissions={['monthly_setup_restriction_setting']} asComponent>
                                <ReadOnlyGuard>
                                    <div className='d-flex align-items-center gap-2'>
                                        <button
                                            className='btn btn-sm btn-outline-primary p-2'
                                            onClick={() => setShowBulkUpdateByDateDialog(true)}
                                            title="Update by Date Range"
                                        >
                                            <IconCalendarStats size={16} />
                                            <span className='ms-1 d-sm-inline'>Update by Date</span>
                                        </button>
                                        <button
                                            className='btn btn-sm btn-info text-white p-2'
                                            onClick={handleOpenBulkEditDialog}
                                            disabled={selectedCells.length === 0}
                                        >
                                            Bulk Edit ({selectedCells.length})
                                        </button>
                                        <button
                                            className='btn btn-sm btn-outline-success p-2'
                                            onClick={() => setShowImportRatesDialog(true)}
                                            title="Import Rates from Excel/CSV"
                                        >
                                            <IconFileImport size={16} />
                                            <span className='ms-1 d-sm-inline'>Import Rates</span>
                                        </button>
                                        {selectedCells.length > 0 && (
                                            <button
                                                className='btn btn-sm btn-outline-danger p-2'
                                                onClick={handleClearSelection}
                                                title="Clear all selections"
                                            >
                                                <IconX size={16} />
                                                <span className="ms-1 d-sm-inline">Clear Selection</span>
                                            </button>
                                        )}
                                    </div>
                                </ReadOnlyGuard>
                            </RequireAuth>
                        </div>

                        {/* Letakkan ini di akhir JSX return Anda, ini adalah Modal/Drawer untuk Mobile */}
                        {isFilterDrawerOpen && (
                            <div
                                className="offcanvas show"
                                style={{
                                    visibility: 'visible',
                                    width: '100vw',        // Lebar 100% dari viewport
                                    height: '100vh',       // Tinggi 100% dari viewport
                                    maxWidth: '100vw',     // Pastikan tidak ada batas lebar maksimal
                                    border: 'none',        // Hapus border samping agar terlihat lebih baik
                                }}
                            >
                                <div className="offcanvas-header">
                                    <h5 className="offcanvas-title">Filters & Actions</h5>
                                    <button type="button" className="btn-close" onClick={() => setFilterDrawerOpen(false)}></button>
                                </div>
                                <div className="offcanvas-body">
                                    {/* Kategori: Navigasi */}
                                    <h6 className="text-muted">NAVIGATION</h6>
                                    <div className="d-flex flex-column gap-3 mb-4">
                                        <div className="d-flex gap-2">
                                            <button className="btn btn-outline-secondary flex-fill" onClick={handlePrevMonth}>
                                                Previous Month
                                            </button>
                                            <button className="btn btn-outline-secondary flex-fill" onClick={handleNextMonth}>
                                                Next Month
                                            </button>
                                        </div>
                                    </div>

                                    {/* Kategori: Tampilan Kalender */}
                                    <h6 className="text-muted">VIEW FILTERS</h6>
                                    <div className="d-flex flex-column gap-3 mb-4">
                                        <RestrictionFilter setRestrictionFilter={setRestrictionFilter} menuZIndex={9999} />
                                        <RoomTypeFilter setRoomTypeFilter={setRoomTypeFilter} menuZIndex={9999} roomTypeQuery={roomTypeQuery} />
                                        <RatePlanFilter setRatePlanFilter={setRatePlanFilter} menuZIndex={9999} ratePlanQuery={ratePlanQuery} />
                                        <button className='btn btn-outline-primary w-100' onClick={() => setShowJumpToDateDialog(true)}>
                                            Jump to Date
                                        </button>
                                    </div>

                                    {/* Kategori: Pengaturan */}
                                    <h6 className="text-muted">SETTINGS</h6>
                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['settings_restrictions']} asComponent>
                                        <ReadOnlyGuard>
                                            <div className="d-flex flex-column gap-3">
                                                <button className='btn btn-primary' onClick={() => setShowRestrictionsDialog(true)}>
                                                    Restrictions & Settings
                                                </button>
                                            </div>
                                        </ReadOnlyGuard>
                                    </RequireAuth>
                                </div>
                            </div>
                        )}

                        {/* ======================================================================= */}
                        {/* BAGIAN QUICK SELECT / BULK SELECTION HELPER                             */}
                        {/* ======================================================================= */}
                        <ReadOnlyGuard mode="hide">
                            <div className="p-3 border-top border-bottom bg-light">
                                <div className="d-flex flex-column flex-lg-row align-items-lg-center gap-2">

                                    {/* Judul untuk memberikan konteks */}
                                    <h6 className="mb-2 mb-lg-0 me-lg-3 text-muted flex-shrink-0 d-lg-none">
                                        <i className="bi bi-magic me-1"></i> {/* Contoh ikon, bisa disesuaikan */}
                                        Quick Select Cells:
                                    </h6>

                                    {/* Kontainer untuk para selector */}
                                    <div className="d-flex flex-column flex-lg-row align-items-stretch gap-3 w-100">
                                        <div style={{ minWidth: '200px' }}> {/* Beri min-width agar tidak terlalu kecil */}
                                            <RatePlanSelector
                                                options={ratePlanSelectorOptions}
                                                value={targetRatePlan}
                                                onChange={setTargetRatePlan}
                                                isLoading={isFetchingMonthlySetup}
                                            />
                                        </div>
                                        <div style={{ minWidth: '200px' }}> {/* Beri min-width agar tidak terlalu kecil */}
                                            <RestrictionTypeSelector
                                                value={selectedRestrictionType}
                                                onChange={setSelectedRestrictionType}
                                            />
                                        </div>
                                        <div className="flex-grow-1">
                                            <DayOfWeekSelector
                                                selectedDays={selectedDays}
                                                onDaysChange={setSelectedDays}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </ReadOnlyGuard>
                        {/* ======================================================================= */}
                        <MonthlySetupTable
                            handlePrevMonth={handlePrevMonth}
                            handleNextMonth={handleNextMonth}
                            handleThisMonth={handleThisMonth}
                            restrictionFilter={restrictionFilter}
                            roomTypeFilter={roomTypeFilter}
                            ratePlanFilter={ratePlanFilter}
                            selectedCells={selectedCells}
                            editedCells={editedCells} // BARU: Pass state 'edited' ke tabel
                            onSelectionChange={(cells) => {
                                // Saat pengguna klik manual, bersihkan target filter
                                setTargetRatePlan(null);
                                setEditedCells([]);
                                setSelectedCells(cells);
                            }}
                            isDragging={isDragging}
                            dragStartCell={dragStartCell}
                            setIsDragging={setIsDragging}
                            setDragStartCell={setDragStartCell}
                            monthlySetup={monthlySetup}
                            occupancy={occupancy}
                            groupedMonths={groupedMonths}
                            isFetching={isFetchingMonthlySetup || isFetchingOccupancy}
                            isError={isErrorMonthlySetup || isErrorOccupancy}
                            readyToLoad={readyToLoad}
                            dynamicPricingGroups={dynamicPricingGroups} // BARU
                            priceAdjustments={priceAdjustments}
                            isReadOnly={isReadOnly}
                        />
                    </div>
                </div>
                <Footer />
            </div>

            {/* <JumpToDateDialog
                show={showJumpToDateDialog}
                onClose={() => setShowJumpToDateDialog(false)}
                onChangeDate={(callback) => {
                    const action = () => {
                        setShowJumpToDateDialog(false);
                        setStartDate(format(new Date(callback.start_date), 'yyyy-MM-dd'));
                        setEndDate(format(new Date(callback.end_date), 'yyyy-MM-dd'));
                    };
                    navigateWithConfirmation(action, 'You have active selections. Jumping to a new date range will clear them. Are you sure?');
                }} /> */}

            <JumpToDateDialogV2
                show={showJumpToDateDialog}
                onClose={() => setShowJumpToDateDialog(false)}
                onChangeDate={(callback) => {
                    const action = () => {
                        setShowJumpToDateDialog(false);
                        setStartDate(format(new Date(callback.start_date), 'yyyy-MM-dd'));
                        setEndDate(format(new Date(callback.end_date), 'yyyy-MM-dd'));
                    };
                    navigateWithConfirmation(action, 'You have active selections. Jumping to a new date range will clear them. Are you sure?');
                }}
            />

            <RequireAuth requiredRoles={['dev']} requiredPermissions={['settings_restrictions']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <RestrictionSettingsDialog
                        show={showRestrictionsDialog}
                        onClose={() => setShowRestrictionsDialog(false)}
                    />
                </ReadOnlyGuard>
            </RequireAuth>

            {showRestrictionSetupDialog && selectedCells.length > 0 && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['monthly_setup_restriction_setting']} asComponent>
                    <ReadOnlyGuard mode="hide">
                        <RestrictionSetup
                            show={showRestrictionSetupDialog}
                            onClose={handleCloseRestrictionSetupDialog}
                            selection={selectedCells}
                            onSuccess={() => {
                                setEditedCells(selectedCells);
                                handleCloseRestrictionSetupDialog();
                                handleClearSelection();
                                setTargetRatePlan(null);
                            }}
                        />
                    </ReadOnlyGuard>
                </RequireAuth>
            )}

            {/* Render modal baru dengan handler onSuccess yang telah diperbarui */}
            {showBulkUpdateByDateDialog && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['monthly_setup_restriction_setting']} asComponent>
                    <ReadOnlyGuard mode="hide">
                        <BulkUpdateByDateRangeDialog
                            show={showBulkUpdateByDateDialog}
                            onClose={() => setShowBulkUpdateByDateDialog(false)}
                            onSuccess={handleBulkUpdateSuccess}
                        />
                    </ReadOnlyGuard>
                </RequireAuth>
            )}

            {showImportRatesDialog && (
                <RequireAuth requiredRoles={['dev']} requiredPermissions={['monthly_setup_restriction_setting']} asComponent>
                    <ReadOnlyGuard mode="hide">
                        <ImportRatesDialog
                            show={showImportRatesDialog}
                            onClose={() => setShowImportRatesDialog(false)}
                            propertyId={user?.active_property_id || ''}
                            onImported={() => {
                                toast.success('Rates imported — refreshing calendar...');
                                queryClient.invalidateQueries();
                            }}
                            onBackground={(id) => {
                                setBackgroundImportId(id);
                            }}
                        />
                    </ReadOnlyGuard>
                </RequireAuth>
            )}

            {/* Floating progress widget (Google-Drive style) saat import di-background. */}
            {backgroundImportId && (
                <RateImportProgress
                    importId={backgroundImportId}
                    onDone={() => {
                        toast.success('Rates imported — refreshing calendar...');
                        queryClient.invalidateQueries();
                    }}
                    onDismiss={() => setBackgroundImportId(null)}
                />
            )}
        </AdminLayout>
    );
}

export default MonthlySetupPage;
